import React, { useState } from 'react';
import { Plus, Trash2, Database, BarChart3, Download, Target } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DataAndReporting, DataEvent } from '../../../types';

interface DataAndReportingSectionProps {
  dataAndReporting: DataAndReporting;
  onUpdate: (dataAndReporting: DataAndReporting) => void;
}

// Preset data events for quick setup
const PRESET_EVENTS = {
  'assessment_complete': {
    when: 'assessment_complete',
    capture: ['student_id', 'assessment_id', 'score', 'time_spent_seconds', 'items_correct'],
    description: 'Track assessment completion and performance'
  },
  'step_complete': {
    when: 'step_complete',
    capture: ['student_id', 'step_id', 'time_spent_seconds', 'completed'],
    description: 'Track timeline step completion'
  },
  'resource_interaction': {
    when: 'resource_interaction',
    capture: ['student_id', 'resource_id', 'action_type', 'duration_seconds'],
    description: 'Track student interactions with resources'
  },
  'quiz_response': {
    when: 'quiz_response',
    capture: ['student_id', 'question_id', 'answer', 'correct', 'time_spent_seconds'],
    description: 'Track individual quiz question responses'
  },
  'engagement_check': {
    when: 'engagement_check',
    capture: ['student_id', 'check_type', 'response', 'timestamp'],
    description: 'Track student engagement and participation'
  },
  'exit_ticket_submit': {
    when: 'exit_ticket_submit',
    capture: ['student_id', 'response', 'understanding_level', 'time_spent_seconds'],
    description: 'Track exit ticket responses'
  }
};

const PRESET_GROUPING_RULES = [
  'score_below_70',
  'engagement_below_50',
  'time_spent_above_120',
  'correct_answers_below_3',
  'participation_below_2'
];

// Get translated preset events
const getPresetEvents = (t: any) => {
  const presetEvents: Record<string, any> = {};
  Object.entries(PRESET_EVENTS).forEach(([key, event]) => {
    presetEvents[key] = {
      ...event,
      description: t(`dataAndReporting.presetEvents.${key}.description`)
    };
  });
  return presetEvents;
}

const getPresetGroupingRules = (t: any) => {
  return PRESET_GROUPING_RULES.map((rule) => t(`dataAndReporting.presetGroupingRules.${rule}`));
}

export const DataAndReportingSection: React.FC<DataAndReportingSectionProps> = ({
  dataAndReporting,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'events' | 'analytics' | 'reporting'>('events');
  const [showPresets, setShowPresets] = useState<Record<string, boolean>>({});

  const updateDataAndReporting = (updates: Partial<DataAndReporting>) => {
    onUpdate({ ...dataAndReporting, ...updates });
  };

  const addEvent = () => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents.push({
      when: '',
      capture: [''],
      description: '',
      trigger_condition: ''
    });
    updateDataAndReporting({ events: newEvents });
  };

  const updateEvent = (index: number, field: keyof DataEvent, value: any) => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents[index] = { ...newEvents[index], [field]: value };
    updateDataAndReporting({ events: newEvents });
  };

  const removeEvent = (index: number) => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents.splice(index, 1);
    updateDataAndReporting({ events: newEvents });
  };

  const addPresetEvent = (key: string, event: any) => {
    const newEvents = [...(dataAndReporting.events || []), event];
    updateDataAndReporting({ events: newEvents });
    setShowPresets(prev => ({ ...prev, events: false }));
  };

  const addCaptureField = (eventIndex: number) => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents[eventIndex].capture.push('');
    updateDataAndReporting({ events: newEvents });
  };

  const updateCaptureField = (eventIndex: number, fieldIndex: number, value: string) => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents[eventIndex].capture[fieldIndex] = value;
    updateDataAndReporting({ events: newEvents });
  };

  const removeCaptureField = (eventIndex: number, fieldIndex: number) => {
    const newEvents = [...(dataAndReporting.events || [])];
    newEvents[eventIndex].capture.splice(fieldIndex, 1);
    updateDataAndReporting({ events: newEvents });
  };

  const TabButton = ({ id, label, icon: Icon, count }: { id: string; label: string; icon: any; count?: number }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id as any)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        activeTab === id
          ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('dataAndReporting.dataAndReporting')}</h3>
              <p className="text-sm text-gray-600">{t('dataAndReporting.configureDataAndReporting')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-2">
          <TabButton
            id="events"
            label={t('dataAndReporting.trackingEvents')}
            icon={Target}
            count={dataAndReporting.events?.length || 0}
          />
          <TabButton
            id="analytics"
            label={t('navigation.analytics')}
            icon={BarChart3}
          />
          <TabButton
            id="reporting"
            label={t('dataAndReporting.reportingSettings')}
            icon={Download}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-medium text-gray-900">{t('dataAndReporting.dataCollectionEvents')}</h4>
                <p className="text-sm text-gray-600">{t('dataAndReporting.configureDataCollectionEvents')}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPresets(prev => ({ ...prev, events: !prev.events }))}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('dataAndReporting.presets')}</span>
                </button>
                <button
                  type="button"
                  onClick={addEvent}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('dataAndReporting.addCustom')}</span>
                </button>
              </div>
            </div>

            {/* Preset Events */}
            {showPresets.events && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="text-sm font-medium text-blue-900 mb-3">{t('dataAndReporting.quickAddPresetEvents')}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(getPresetEvents(t)).map(([key, event]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => addPresetEvent(key, event)}
                      className="text-left p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-blue-900">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="text-xs text-blue-700 mt-1">{event.description}</div>
                      <div className="text-xs text-blue-600 mt-2">
                        {t('dataAndReporting.captures')}: {event.capture.join(', ')}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Events */}
            <div className="space-y-4">
              {(dataAndReporting.events || []).map((event, eventIndex) => (
                <div key={eventIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-medium text-gray-900">{t('dataAndReporting.event')} {eventIndex + 1}</h5>
                    <button
                      type="button"
                      onClick={() => removeEvent(eventIndex)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('dataAndReporting.whenToCapture')}</label>
                      {/* TODO: add a dropdown of all the events that can be captured */}
                      <select
                        value={event.when || ''}
                        onChange={(e) => updateEvent(eventIndex, 'when', e.target.value)}
                        className="input-field"
                      >
                        {Object.entries(getPresetEvents(t)).map(([key, event]) => (
                          <option key={key} value={event.when}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('dataAndReporting.description')}</label>
                      <input
                        type="text"
                        value={event.description || ''}
                        onChange={(e) => updateEvent(eventIndex, 'description', e.target.value)}
                        placeholder={t('dataAndReporting.briefDescriptionOfThisEvent')}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('dataAndReporting.dataToCapture')}</label>
                    <div className="space-y-2">
                      {(event.capture || []).map((field, fieldIndex) => (
                        <div key={fieldIndex} className="flex items-center space-x-2">
                          {/* TODO: add a dropdown of all the fields that can be captured */}
                            <select
                              key={field}
                              value={field}
                              onChange={(e) => updateCaptureField(eventIndex, fieldIndex, e.target.value)}
                              className="input-field flex-1"
                            >
                              {PRESET_EVENTS[event.when as keyof typeof PRESET_EVENTS]?.capture.map((capture) => (
                                <option key={capture} value={capture}>{capture}</option>
                              ))}
                            </select>
                          <button
                            type="button"
                            onClick={() => removeCaptureField(eventIndex, fieldIndex)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addCaptureField(eventIndex)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        <Plus className="w-3 h-3" />
                        <span>{t('dataAndReporting.addField')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {(dataAndReporting.events || []).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('dataAndReporting.noDataEventsConfigured')}</p>
                  <p className="text-xs mt-1">{t('dataAndReporting.addEventsToTrack')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">{t('dataAndReporting.analyticsSettings')}</h4>
              <p className="text-sm text-gray-600 mb-6">{t('dataAndReporting.configureAnalytics')}</p>
            </div>

            {/* Analytics Toggle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">{t('dataAndReporting.enableAnalytics')}</h5>
                    <p className="text-xs text-gray-600">{t('dataAndReporting.trackStudentPerformance')}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataAndReporting.analytics_enabled || false}
                    onChange={(e) => updateDataAndReporting({ analytics_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Student Tracking Options */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-900">{t('dataAndReporting.studentTrackingOptions')}</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dataAndReporting.student_tracking?.progress_tracking || false}
                      onChange={(e) => updateDataAndReporting({
                        student_tracking: {
                          ...dataAndReporting.student_tracking,
                          progress_tracking: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t('dataAndReporting.progressTracking')}</div>
                      <div className="text-xs text-gray-600">{t('dataAndReporting.trackCompletionRates')}</div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dataAndReporting.student_tracking?.time_tracking || false}
                      onChange={(e) => updateDataAndReporting({
                        student_tracking: {
                          ...dataAndReporting.student_tracking,
                          time_tracking: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t('dataAndReporting.timeTracking')}</div>
                      <div className="text-xs text-gray-600">{t('dataAndReporting.monitorTimeSpent')}</div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dataAndReporting.student_tracking?.engagement_metrics || false}
                      onChange={(e) => updateDataAndReporting({
                        student_tracking: {
                          ...dataAndReporting.student_tracking,
                          engagement_metrics: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t('dataAndReporting.engagementMetrics')}</div>
                      <div className="text-xs text-gray-600">{t('dataAndReporting.trackInteractions')}</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reporting' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">{t('dataAndReporting.reportingConfiguration')}</h4>
              <p className="text-sm text-gray-600 mb-6">{t('dataAndReporting.configureReporting')}</p>
            </div>

            {/* Reporting Frequency */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('dataAndReporting.reportingFrequency')}</label>
              <select
                value={dataAndReporting.reporting_frequency || 'end_of_lesson'}
                onChange={(e) => updateDataAndReporting({ reporting_frequency: e.target.value as any })}
                className="input-field"
              >
                <option value="real_time">{t('dataAndReporting.realTime')}</option>
                <option value="end_of_lesson">{t('dataAndReporting.endOfLesson')}</option>
                <option value="daily">{t('dataAndReporting.daily')}</option>
                <option value="weekly">{t('dataAndReporting.weekly')}</option>
              </select>
            </div>

            {/* Export Formats */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('dataAndReporting.exportFormats')}</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(['csv', 'pdf', 'json'] as const).map((format) => (
                  <label key={format} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dataAndReporting.export_formats?.includes(format) || false}
                      onChange={(e) => {
                        const currentFormats = dataAndReporting.export_formats || [];
                        const newFormats = e.target.checked
                          ? [...currentFormats, format]
                          : currentFormats.filter(f => f !== format);
                        updateDataAndReporting({ export_formats: newFormats });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{format.toUpperCase()}</div>
                      <div className="text-xs text-gray-600">
                        {format === 'csv' && t('dataAndReporting.spreadsheetFormat')}
                        {format === 'pdf' && t('dataAndReporting.printableReports')}
                        {format === 'json' && t('dataAndReporting.dataExport')}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Next Lesson Grouping Rule */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">{t('dataAndReporting.nextLessonGroupingRule')}</label>
                <button
                  type="button"
                  onClick={() => setShowPresets(prev => ({ ...prev, rules: !prev.rules }))}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t('dataAndReporting.viewPresets')}
                </button>
              </div>

              {/* Preset Rules */}
              {showPresets.rules && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h6 className="text-sm font-medium text-green-900 mb-3">{t('dataAndReporting.quickAddPresetRules')}</h6>
                  <div className="space-y-2">
                    {getPresetGroupingRules(t).map((rule, index) => (
                      <button
                        type="button"
                        key={index}
                        onClick={() => updateDataAndReporting({ next_lesson_grouping_rule: rule })}
                        className="w-full text-left p-3 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                      >
                        <div className="text-sm text-green-900">{rule}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <textarea
                value={dataAndReporting.next_lesson_grouping_rule || ''}
                onChange={(e) => updateDataAndReporting({ next_lesson_grouping_rule: e.target.value })}
                placeholder={t('dataAndReporting.nextLessonGroupingRulePlaceholder')}
                rows={3}
                className="input-field"
              />
              <p className="text-xs text-gray-500">
                {t('dataAndReporting.nextLessonGroupingRuleDescription')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
